<template>
  <div>
    <b-row class="mb-2">
      <b-col>
        <b-card class="bg-gradient-primary text-white">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <feather-icon icon="MapPinIcon" size="28" class="mr-1" />
              <div>
                <h2 class="text-white mb-0">{{ isEdit ? 'Editar Endereço do Aluno' : 'Novo Endereço de Aluno' }}</h2>
                <small class="text-white-50">Cadastro de domicílio e dados de contato para visitas domiciliares de campo</small>
              </div>
            </div>
            <b-button variant="outline-light" size="sm" :to="{ name: 'AlunosEnderecosLista' }">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              Voltar
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-form @submit.prevent="salvar">
      <!-- Card 1: Identificação do Aluno e Escola -->
      <b-card class="mb-2">
        <h5 class="text-primary font-weight-bolder mb-1">
          <feather-icon icon="UserIcon" class="mr-50" />
          Dados do Aluno e Contato
        </h5>
        <hr class="mb-2" />

        <b-row>
          <b-col md="6">
            <b-form-group label="Nome Completo do Aluno *" label-for="nomeAluno">
              <b-form-input
                id="nomeAluno"
                v-model="form.nomeAluno"
                placeholder="Nome do aluno"
                required
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Nome do Responsável" label-for="nomeResponsavel">
              <b-form-input
                id="nomeResponsavel"
                v-model="form.nomeResponsavel"
                placeholder="Pai, mãe ou responsável legal"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Telefone / WhatsApp" label-for="telefone">
              <b-form-input
                id="telefone"
                v-model="form.telefone"
                placeholder="(81) 90000-0000"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Escola" label-for="schoolId">
              <v-select
                id="schoolId"
                v-model="form.schoolId"
                :options="escolaOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Selecione a escola (opcional)"
                @input="onEscolaChange"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Turma Cadastrada" label-for="classroomId">
              <v-select
                id="classroomId"
                v-model="form.classroomId"
                :options="turmaOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Selecione a turma"
                :disabled="!form.schoolId"
              />
            </b-form-group>
          </b-col>

          <b-col md="12" v-if="!form.classroomId">
            <b-form-group label="Ou digite a Turma / Ano (se não constar na lista)" label-for="turmaTexto">
              <b-form-input
                id="turmaTexto"
                v-model="form.turmaTexto"
                placeholder="Ex: 8º Ano B - Tarde"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <!-- Card 2: Endereço e Localização -->
      <b-card class="mb-2">
        <h5 class="text-primary font-weight-bolder mb-1">
          <feather-icon icon="HomeIcon" class="mr-50" />
          Endereço do Domicílio
        </h5>
        <hr class="mb-2" />

        <b-row>
          <b-col md="4">
            <b-form-group label="Código do Domicílio" label-for="codigoDomicilio">
              <b-form-input
                id="codigoDomicilio"
                v-model="form.codigoDomicilio"
                placeholder="Ex: DOM-001 / Código da etiqueta"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="CEP" label-for="cep">
              <b-input-group>
                <b-form-input
                  id="cep"
                  v-model="form.cep"
                  placeholder="00000-000"
                  @blur="buscarCep"
                />
                <b-input-group-append>
                  <b-button variant="outline-primary" @click="buscarCep" :disabled="isLoadingCep">
                    <b-spinner small v-if="isLoadingCep"></b-spinner>
                    <feather-icon icon="SearchIcon" v-else />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Número" label-for="numero">
              <b-form-input id="numero" v-model="form.numero" placeholder="Nº da casa" />
            </b-form-group>
          </b-col>

          <b-col md="8">
            <b-form-group label="Logradouro / Endereço" label-for="endereco">
              <b-form-input id="endereco" v-model="form.endereco" placeholder="Rua, Avenida, etc." />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Complemento" label-for="complemento">
              <b-form-input id="complemento" v-model="form.complemento" placeholder="Apto, Bloco, etc." />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Bairro" label-for="bairro">
              <b-form-input id="bairro" v-model="form.bairro" placeholder="Bairro" />
            </b-form-group>
          </b-col>

          <b-col md="5">
            <b-form-group label="Município" label-for="municipio">
              <b-form-input id="municipio" v-model="form.municipio" placeholder="Município" />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group label="Estado (UF)" label-for="estado">
              <b-form-input id="estado" v-model="form.estado" placeholder="UF (ex: PE)" />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group label="Ponto de Referência" label-for="pontoReferencia">
              <b-form-input
                id="pontoReferencia"
                v-model="form.pontoReferencia"
                placeholder="Ex: Próximo à padaria São José / Em frente ao campinho"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group label="Observações Adicionais" label-for="observacoes">
              <b-form-textarea
                id="observacoes"
                v-model="form.observacoes"
                placeholder="Informações adicionais sobre o domicílio ou aluno..."
                rows="2"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <!-- Botões de Ação -->
      <div class="d-flex justify-content-end mb-4">
        <b-button variant="outline-secondary" class="mr-1" :to="{ name: 'AlunosEnderecosLista' }">
          Cancelar
        </b-button>
        <b-button variant="primary" type="submit">
          <feather-icon icon="SaveIcon" class="mr-50" />
          {{ isEdit ? 'Salvar Alterações' : 'Cadastrar Endereço' }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import alunoEnderecoService from '@/services/alunoEnderecoService'
import schoolService from '@/services/schoolService'
import classroomService from '@/services/classroomService'
import { hideSpinner, showSpinner } from '@/services/spinService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BRow, BCol, BCard, BButton, BForm, BFormGroup, BFormInput,
  BFormTextarea, BInputGroup, BInputGroupAppend, BSpinner
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      isEdit: false,
      isLoadingCep: false,
      escolaOptions: [],
      turmaOptions: [],
      form: {
        id: null,
        nomeAluno: '',
        nomeResponsavel: '',
        telefone: '',
        schoolId: null,
        classroomId: null,
        turmaTexto: '',
        codigoDomicilio: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        municipio: '',
        estado: '',
        pontoReferencia: '',
        observacoes: '',
      },
    }
  },
  async mounted() {
    await this.carregarEscolas()
    if (this.$route.params.id) {
      this.isEdit = true
      await this.carregarDados(this.$route.params.id)
    }
  },
  methods: {
    async carregarEscolas() {
      try {
        const response = await schoolService.getAll({ page: 1, itemsPerPage: 100 })
        const list = response.content || response || []
        this.escolaOptions = list.map(e => ({ value: e.id, text: e.name }))
      } catch (error) {
        console.error('Erro ao carregar escolas:', error)
      }
    },
    formatarNomeTurma(c) {
      if (!c) return ''
      const turma = (c.turma || '').trim()
      const ano = (c.ano || '').trim()
      const turno = (c.turno || '').trim()

      if (/ano|º|ª/i.test(turma)) {
        return turno ? `${turma} - ${turno}` : turma
      }

      if (/ano|º|ª/i.test(ano)) {
        const nome = turma ? `${ano} ${turma}` : ano
        return turno ? `${nome} - ${turno}` : nome
      }

      if (/^\d+$/.test(ano)) {
        const nome = turma ? `${ano}º ano ${turma}` : `${ano}º ano`
        return turno ? `${nome} - ${turno}` : nome
      }

      const partes = [ano, turma].filter(Boolean).join(' ')
      return turno ? `${partes} - ${turno}` : partes
    },
    async onEscolaChange(schoolId) {
      this.form.classroomId = null
      this.turmaOptions = []
      if (schoolId) {
        try {
          const res = await classroomService.getAll({ schoolId, page: 1, itemsPerPage: 100 })
          const list = res.content || res || []
          this.turmaOptions = list.map(c => ({
            value: c.id,
            text: this.formatarNomeTurma(c),
          }))
        } catch (error) {
          console.error('Erro ao carregar turmas:', error)
        }
      }
    },
    async carregarDados(id) {
      showSpinner()
      try {
        const data = await alunoEnderecoService.getById(id)
        if (data) {
          this.form = {
            id: data.id,
            nomeAluno: data.nomeAluno || '',
            nomeResponsavel: data.nomeResponsavel || '',
            telefone: data.telefone || '',
            schoolId: data.schoolId || null,
            classroomId: data.classroomId || null,
            turmaTexto: data.turmaTexto || '',
            codigoDomicilio: data.codigoDomicilio || '',
            cep: data.cep || '',
            endereco: data.endereco || '',
            numero: data.numero || '',
            complemento: data.complemento || '',
            bairro: data.bairro || '',
            municipio: data.municipio || '',
            estado: data.estado || '',
            pontoReferencia: data.pontoReferencia || '',
            observacoes: data.observacoes || '',
          }
          if (data.schoolId) {
            const res = await classroomService.getAll({ schoolId: data.schoolId, page: 1, itemsPerPage: 100 })
            const list = res.content || res || []
            this.turmaOptions = list.map(c => ({
              value: c.id,
              text: this.formatarNomeTurma(c),
            }))
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dados do endereço',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        hideSpinner()
      }
    },
    async buscarCep() {
      if (!this.form.cep) return
      const cepOnlyNumbers = this.form.cep.replace(/\D/g, '')
      if (cepOnlyNumbers.length !== 8) return

      this.isLoadingCep = true
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepOnlyNumbers}/json/`)
        const data = await response.json()
        if (!data.erro) {
          this.form.endereco = data.logradouro || ''
          this.form.bairro = data.bairro || ''
          this.form.municipio = data.localidade || ''
          this.form.estado = data.uf || ''

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'CEP encontrado!',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'CEP não encontrado',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        console.error('Erro ao consultar CEP:', error)
      } finally {
        this.isLoadingCep = false
      }
    },
    async salvar() {
      if (!this.form.nomeAluno || !this.form.nomeAluno.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Por favor, informe o nome do aluno.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }

      showSpinner()
      try {
        if (this.isEdit) {
          await alunoEnderecoService.update(this.form)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Endereço atualizado com sucesso!',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          await alunoEnderecoService.create(this.form)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Endereço cadastrado com sucesso!',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
        this.$router.push({ name: 'AlunosEnderecosLista' })
      } catch (error) {
        const message = error.response?.data?.message || 'Erro ao salvar endereço.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        hideSpinner()
      }
    },
  },
}
</script>
